import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActionList, ActionMenu, AnchoredOverlay, Box, Button, ConfirmationDialog, IconButton, Link, Select, Text } from "@primer/react";
import { CheckCircleFillIcon, InfoIcon, KebabHorizontalIcon, SearchIcon } from "@primer/octicons-react";
import { AccessTriggerPill } from "./AccessTriggerPill";
import { AccessAvatar } from "./AccessAvatar";
import { useEmailLookup } from "./useEmailLookup";
import type { Grantee, GranteeStatus, ResourceAccessProps } from "./types";

// Widget collapses pending and unconfirmed into a single "Invited" label —
// both mean "user isn't fully set up yet" from the admin's POV. The full
// 4-state distinction is preserved in Manage Users where it belongs.
// Disabled users are filtered out at the model layer (Event/Report#access_grants)
// so they never reach this map.
const STATUS_LABEL: Partial<Record<GranteeStatus, string>> = {
  pending: "Invited",
  unconfirmed: "Invited",
};

const PULSE_MS = 1200;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const csrfToken = (): string =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";

// GR-1590 D4: `canManage` (component-wide) and `removable` (per-grantee)
// are UI hints only — they control which controls render, NOT whether
// the server accepts the mutation. The grant/revoke endpoints in this
// popover (`endpoints.grant`, `endpoints.revoke`, `endpoints.assignRole`)
// are independently re-authorized server-side via the resource's Pundit
// policy (`manage_access?` / `users:manage_permissions`). A user who
// somehow sees stale `canManage: true` props will hit a 403 on submit
// rather than silently mutating state. Don't treat these props as
// authorization gates — the server is the gate.
export const ResourceAccessPopover: React.FC<ResourceAccessProps> = ({
  triggerLabel,
  dialogTitle,
  grantees,
  adminCount,
  canManage,
  endpoints,
  roleOptions,
  defaultRoleValue,
  resourceKind,
  eventId,
  copy,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedRole, setSelectedRole] = useState(defaultRoleValue);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Grantee | null>(null);
  const [pulsedRowId, setPulsedRowId] = useState<number | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const queryInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const revokeFormRef = useRef<HTMLFormElement>(null);

  const granteeEmailToId = useMemo(
    () => new Map(grantees.map((g) => [g.email.toLowerCase().trim(), g.id])),
    [grantees],
  );

  // Lookup only fires when the query parses as an email.
  const isEmail = EMAIL_RE.test(query.trim());
  const lookup = useEmailLookup({
    lookupUrl: endpoints.lookup,
    email: isEmail ? query : "",
    granteeEmailToId,
    resourceKind,
    eventId,
  });

  const selectedRoleLabel = useMemo(
    () => roleOptions.find((r) => r.value === selectedRole)?.label ?? "",
    [roleOptions, selectedRole],
  );

  // Auto-focus the input when the popover opens.
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => queryInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  // Scroll-to + pulse the matching row when the typed email is already in the list.
  useEffect(() => {
    if (lookup.kind !== "already_in_list") return;
    setPulsedRowId(lookup.granteeId);
    const el = document.querySelector(`[data-grantee-id="${lookup.granteeId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const t = setTimeout(() => setPulsedRowId(null), PULSE_MS);
    return () => clearTimeout(t);
  }, [lookup]);

  // Pre-select the looked-up user's existing org role when we find a known
  // user, so admins don't have to re-pick the user's role for the resource.
  // Falls back to the resource default when the user's org role isn't one of
  // the offered options for this widget (e.g. report widget locks to
  // report_viewer; org admins never reach this branch — they're routed to
  // implicit_access upstream).
  //
  // Keyed on lookup.kind + the looked-up user id so the effect only fires
  // on a *transition* into "known" (or onto a different known user). It must
  // NOT depend on roleOptions / defaultRoleValue identity — those are read
  // from refs to avoid clobbering the admin's manual role selection if the
  // parent re-renders with new array refs.
  const roleOptionsRef = useRef(roleOptions);
  const defaultRoleValueRef = useRef(defaultRoleValue);
  roleOptionsRef.current = roleOptions;
  defaultRoleValueRef.current = defaultRoleValue;

  const knownUserId = lookup.kind === "known" ? lookup.user.id : null;
  const knownOrgRoleValue = lookup.kind === "known" ? lookup.user.orgRoleValue : null;
  useEffect(() => {
    if (knownUserId === null) return;
    const opts = roleOptionsRef.current;
    if (knownOrgRoleValue && opts.some((r) => r.value === knownOrgRoleValue)) {
      setSelectedRole(knownOrgRoleValue);
    } else {
      setSelectedRole(defaultRoleValueRef.current);
    }
  }, [knownUserId, knownOrgRoleValue]);

  const openLeaveConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    setConfirmLeaveOpen(true);
  };
  const handleLeaveConfirm = (gesture: "confirm" | "close-button" | "cancel" | "escape") => {
    setConfirmLeaveOpen(false);
    if (gesture === "confirm") window.location.href = "/admin/users";
  };

  const handleRevokeConfirm = (gesture: "confirm" | "close-button" | "cancel" | "escape") => {
    if (gesture === "confirm") {
      // Submit the hidden DELETE form before clearing the target so the
      // form's action URL is still valid at submit time.
      revokeFormRef.current?.submit();
    } else {
      setRevokeTarget(null);
    }
  };

  // Filter the grantees client-side by the same input. When the query is an
  // email, we rely on the lookup card; the list filter stays inert (full
  // list) so the user can still browse — that matches the design.
  const filteredGrantees = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || isEmail) return grantees;
    return grantees.filter(
      (g) => g.name.toLowerCase().includes(q) || g.email.toLowerCase().includes(q),
    );
  }, [grantees, query, isEmail]);

  const canSubmitKnown = lookup.kind === "known";
  const canSubmitUnknown =
    lookup.kind === "unknown" && firstName.trim().length > 0 && lastName.trim().length > 0;

  const submit = () => formRef.current?.submit();
  const sharedButtonLabel = copy?.inviteCta ?? "Share";
  const inviteAsLabel = `Invite as ${selectedRoleLabel}`;

  return (
    <>
      <AccessTriggerPill
        ref={anchorRef}
        label={triggerLabel}
        grantees={grantees}
        expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      <AnchoredOverlay
        renderAnchor={null}
        anchorRef={anchorRef}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        width="large"
        height="auto"
        side="outside-bottom"
        align="end"
        overlayProps={{ sx: { borderRadius: "6px" } }}
      >
        <Box
          aria-label={dialogTitle}
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "min(70vh, 720px)",
          }}
        >
          <Box
            as="form"
            ref={formRef}
            method="post"
            action={endpoints.grant}
            sx={{ display: "block", flex: "0 0 auto" }}
          >
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <input type="hidden" name="email" value={query.trim()} />
            <input type="hidden" name="role_value" value={selectedRole} />
            {lookup.kind === "unknown" && (
              <>
                <input type="hidden" name="first_name" value={firstName} />
                <input type="hidden" name="last_name" value={lastName} />
              </>
            )}

            {/* Single input — filters grantees and triggers email lookup */}
            {canManage && (
              <Box sx={{ paddingX: 3, paddingTop: 3, paddingBottom: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    border: "1px solid",
                    borderColor: "border.default",
                    borderRadius: 2,
                    paddingX: 2,
                    backgroundColor: "canvas.default",
                    "&:focus-within": {
                      borderColor: "accent.emphasis",
                      boxShadow: "0 0 0 3px rgba(9, 105, 218, 0.30)",
                    },
                  }}
                >
                  <Box sx={{ color: "fg.muted", display: "flex" }}>
                    <SearchIcon size={14} />
                  </Box>
                  <Box
                    as="input"
                    ref={queryInputRef}
                    type="text"
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                    placeholder={copy?.emailPlaceholder ?? "Search for a user with access, or invite via email"}
                    autoComplete="off"
                    sx={{
                      flex: 1,
                      height: "38px",
                      border: 0,
                      outline: "none",
                      background: "transparent",
                      fontSize: 1,
                      fontFamily: "inherit",
                      color: "fg.default",
                      minWidth: 0,
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Lookup result card — only when the query is an email */}
            {canManage && isEmail && (
              <Box sx={{ paddingX: 3, paddingBottom: 2 }}>
                {lookup.kind === "looking" && (
                  <Text sx={{ fontSize: 0, color: "fg.muted" }}>Looking up…</Text>
                )}

                {lookup.kind === "implicit_access" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      paddingX: 2,
                      paddingY: 2,
                      backgroundColor: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 2,
                    }}
                  >
                    <AccessAvatar src={lookup.user.avatarUrl} alt={lookup.user.name} size={28} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Text sx={{ display: "block", fontSize: 1, fontWeight: 600, color: "fg.default" }}>
                        {lookup.user.name}
                      </Text>
                      <Text sx={{ display: "block", fontSize: 0, color: "fg.muted" }}>
                        Already has access:{" "}
                        {lookup.reason === "admin"
                          ? "Organisation Administrator"
                          : "All reports permission"}
                      </Text>
                    </Box>
                  </Box>
                )}

                {lookup.kind === "known" && (
                  <Box
                    sx={{
                      paddingX: 2,
                      paddingY: 2,
                      backgroundColor: "accent.subtle",
                      border: "1px solid",
                      borderColor: "accent.muted",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <AccessAvatar src={lookup.user.avatarUrl} alt={lookup.user.name} size={28} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Text sx={{ display: "block", fontSize: 1, fontWeight: 600, color: "fg.default" }}>
                          {lookup.user.name}
                        </Text>
                        <Text sx={{ display: "block", fontSize: 0, color: "fg.muted" }}>
                          {lookup.user.inCurrentOrg
                            ? "Existing org member"
                            : "Existing user, will be added to your org"}
                          {" · "}will gain {selectedRoleLabel} access
                        </Text>
                      </Box>
                      <Box sx={{ color: "success.fg", display: "flex" }}>
                        <CheckCircleFillIcon size={16} />
                      </Box>
                    </Box>
                    {roleOptions.length > 1 && (
                      <Box sx={{ marginTop: 2 }}>
                        <Text as="label" sx={{ display: "block", fontSize: 0, color: "fg.muted", marginBottom: 1 }}>
                          Role
                        </Text>
                        <Select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          sx={{ width: "100%" }}
                        >
                          {roleOptions.map((r) => (
                            <Select.Option key={r.value} value={r.value}>
                              {r.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Box>
                    )}
                    <Box sx={{ marginTop: 2, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        variant="primary"
                        onClick={() => canSubmitKnown && submit()}
                        disabled={!canSubmitKnown}
                      >
                        {sharedButtonLabel}
                      </Button>
                    </Box>
                  </Box>
                )}

                {lookup.kind === "unknown" && (
                  <Box
                    sx={{
                      padding: 3,
                      backgroundColor: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
                      <Box sx={{ color: "fg.muted", display: "flex" }}>
                        <InfoIcon size={14} />
                      </Box>
                      <Text sx={{ fontSize: 0, color: "fg.muted" }}>
                        New user, they'll be added to your organisation and granted access to this {copy?.resourceWord ?? "report"} only.
                      </Text>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, marginBottom: 2 }}>
                      <Box
                        as="input"
                        type="text"
                        value={firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                        placeholder="First name"
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                          height: "38px",
                          paddingX: 2,
                          backgroundColor: "canvas.default",
                          border: "1px solid",
                          borderColor: "border.default",
                          borderRadius: 2,
                          fontSize: 1,
                          fontFamily: "inherit",
                          outline: "none",
                          "&:focus": { borderColor: "accent.emphasis" },
                        }}
                      />
                      <Box
                        as="input"
                        type="text"
                        value={lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                        placeholder="Last name"
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                          height: "38px",
                          paddingX: 2,
                          backgroundColor: "canvas.default",
                          border: "1px solid",
                          borderColor: "border.default",
                          borderRadius: 2,
                          fontSize: 1,
                          fontFamily: "inherit",
                          outline: "none",
                          "&:focus": { borderColor: "accent.emphasis" },
                        }}
                      />
                    </Box>
                    {roleOptions.length > 1 && (
                      <Box sx={{ marginBottom: 2 }}>
                        <Text as="label" sx={{ display: "block", fontSize: 0, color: "fg.muted", marginBottom: 1 }}>
                          Role
                        </Text>
                        <Select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          sx={{ width: "100%" }}
                        >
                          {roleOptions.map((r) => (
                            <Select.Option key={r.value} value={r.value}>
                              {r.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Box>
                    )}
                    <Button
                      size="large"
                      variant="primary"
                      block
                      onClick={() => canSubmitUnknown && submit()}
                      disabled={!canSubmitUnknown}
                    >
                      {inviteAsLabel}
                    </Button>
                  </Box>
                )}

                {lookup.kind === "already_in_list" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      paddingX: 2,
                      paddingY: 2,
                      backgroundColor: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ color: "fg.muted", display: "flex" }}>
                      <InfoIcon size={14} />
                    </Box>
                    <Text sx={{ flex: 1, fontSize: 0, color: "fg.muted" }}>
                      {grantees.find((g) => g.id === lookup.granteeId)?.name ?? "This user"} already has access, see below.
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            {/* Helper / context text */}
            {canManage && (
              <Box sx={{ paddingX: 3, paddingBottom: 3 }}>
                <Text
                  sx={{
                    display: "block",
                    fontSize: 0,
                    color: "fg.muted",
                    lineHeight: 1.45,
                  }}
                >
                  {copy?.helperText ?? (
                    <>
                      Access is scoped to this {copy?.resourceWord ?? "report"} only. Broader access and roles can be managed at{" "}
                      <Link
                        href="/admin/users"
                        onClick={openLeaveConfirm}
                        sx={{ color: "accent.fg", cursor: "pointer", textDecoration: "underline" }}
                      >
                        organisation level
                      </Link>
                      .
                    </>
                  )}
                </Text>
              </Box>
            )}
          </Box>

          {/* Access list — single flat view; the role label tells you why
              someone is on the list (e.g. "Admin" = inherited). Flex: 1
              so the list shrinks to fit between the (fixed) header and
              footer instead of pushing the popover off-screen. */}
          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: "border.default",
              paddingX: 3,
              paddingY: 2,
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <Box>
              {filteredGrantees.length === 0 ? (
                <Box sx={{ paddingY: 4, textAlign: "center" }}>
                  <Text sx={{ fontSize: 1, color: "fg.muted" }}>
                    {query.trim().length > 0 && !isEmail
                      ? `No matches for "${query.trim()}"`
                      : copy?.emptyState ?? "No one has direct access yet."}
                  </Text>
                </Box>
              ) : (
                filteredGrantees.map((g) => (
                  <GranteeRow
                    key={g.id}
                    grantee={g}
                    canManage={canManage}
                    pulsed={pulsedRowId === g.id}
                    onRevokeClick={setRevokeTarget}
                  />
                ))
              )}
            </Box>
          </Box>

          {/* Admin acknowledgement footer */}
          {adminCount > 0 && (
            <Box
              sx={{
                flex: "0 0 auto",
                paddingX: 4,
                paddingY: 3,
                borderTop: "1px solid",
                borderColor: "border.default",
                backgroundColor: "canvas.subtle",
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <Box sx={{ color: "fg.muted", display: "flex", paddingTop: "2px" }}>
                <InfoIcon size={12} />
              </Box>
              <Text sx={{ fontSize: 0, color: "fg.muted", lineHeight: 1.45 }}>
                {copy?.adminFooter ??
                  "Admins and users with report permissions can access this report."}
              </Text>
            </Box>
          )}
        </Box>
      </AnchoredOverlay>

      {confirmLeaveOpen && (
        <ConfirmationDialog
          title="Leave this report?"
          confirmButtonContent="Continue to Manage Users"
          onClose={handleLeaveConfirm}
        >
          You're about to leave this report and go to organisation user &amp; role management. Any unsaved changes here will be lost.
        </ConfirmationDialog>
      )}

      {revokeTarget && (
        <>
          <Box
            as="form"
            ref={revokeFormRef}
            method="post"
            action={`${endpoints.revokePrefix}${revokeTarget.id}`}
            sx={{ display: "none" }}
          >
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <input type="hidden" name="_method" value="delete" />
          </Box>
          <ConfirmationDialog
            title={`Remove ${revokeTarget.name}'s access?`}
            confirmButtonContent="Remove access"
            confirmButtonType="danger"
            onClose={handleRevokeConfirm}
          >
            {revokeTarget.name} will lose direct access to this report. They may still see it if they're an organisation admin or have the All reports permission.
          </ConfirmationDialog>
        </>
      )}
    </>
  );
};

const GranteeRow: React.FC<{
  grantee: Grantee;
  canManage: boolean;
  pulsed: boolean;
  onRevokeClick?: (g: Grantee) => void;
}> = ({ grantee: g, canManage, pulsed, onRevokeClick }) => (
  <Box
    data-grantee-id={g.id}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      paddingY: "6px",
      paddingX: "10px",
      borderRadius: 2,
      backgroundColor: pulsed ? "accent.subtle" : "transparent",
      transition: pulsed ? "none" : "background-color 600ms ease-out",
      "&:hover": { backgroundColor: pulsed ? "accent.subtle" : "canvas.subtle" },
      "&:hover .row-actions": { opacity: 1 },
    }}
  >
    <AccessAvatar src={g.avatarUrl} alt={g.name} size={22} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Text
        sx={{
          display: "block",
          fontSize: 1,
          fontWeight: 400,
          color: "fg.default",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {g.name}
      </Text>
    </Box>
    <Text sx={{ fontSize: 0, color: "fg.muted", whiteSpace: "nowrap", paddingX: 1 }}>
      {STATUS_LABEL[g.status] ?? g.roleLabel}
    </Text>
    {canManage && (
      <Box sx={{ width: "28px", height: "28px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {g.removable && (
          <Box className="row-actions" sx={{ opacity: 0, transition: "opacity 120ms" }}>
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton
                  icon={KebabHorizontalIcon}
                  aria-label={`Access options for ${g.name}`}
                  variant="invisible"
                  size="small"
                  unsafeDisableTooltip
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay align="end">
                <ActionList>
                  <ActionList.Item variant="danger" onSelect={() => onRevokeClick?.(g)}>
                    Remove access
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </Box>
        )}
      </Box>
    )}
  </Box>
);
